import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import AnimeCard from "@/components/feature/AnimeCard";
import Pagination from "@/components/feature/Pagination";
import FilterPanel from "@/pages/category/components/FilterPanel";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { animeList } from "@/mocks/anime";
import type { Anime } from "@/types/content";
import { PAGE_SIZE, filterAnime, paginate, totalPages } from "@/utils/content";

const sortOptions = [
  { key: "update", label: "更新时间" },
  { key: "popular", label: "热门度" },
  { key: "rating", label: "评分" },
];

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const tags = (searchParams.get("type") || "").split(",").filter(Boolean);
  const regions = (searchParams.get("region") || "").split(",").filter(Boolean);
  const years = (searchParams.get("year") || "").split(",").filter(Boolean);
  const sort = searchParams.get("sort") || "update";
  const page = Number(searchParams.get("page") || "1") || 1;

  const selection = { tags, regions, years };

  useDocumentMeta(
    "动漫天堂 - 动漫分类浏览",
    "动漫天堂动漫分类页，支持按类型、地区、年份多选筛选，并按更新时间、热门度与评分排序，帮你快速找到想看的动漫作品。",
    "动漫分类,动漫筛选,动漫类型,动漫大全,动漫推荐,动漫天堂",
    "/category"
  );

  const applyParams = (next: {
    tags?: string[];
    regions?: string[];
    years?: string[];
    sort?: string;
    page?: number;
  }) => {
    const params = new URLSearchParams();
    const nextTags = next.tags ?? tags;
    const nextRegions = next.regions ?? regions;
    const nextYears = next.years ?? years;
    const nextSort = next.sort ?? sort;
    const nextPage = next.page ?? 1;

    if (nextTags.length) params.set("type", nextTags.join(","));
    if (nextRegions.length) params.set("region", nextRegions.join(","));
    if (nextYears.length) params.set("year", nextYears.join(","));
    if (nextSort !== "update") params.set("sort", nextSort);
    if (nextPage > 1) params.set("page", String(nextPage));
    setSearchParams(params);
  };

  const handleToggle = (group: "tags" | "regions" | "years", value: string) => {
    const current = selection[group];
    const nextValues = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    if (group === "tags") applyParams({ tags: nextValues });
    if (group === "regions") applyParams({ regions: nextValues });
    if (group === "years") applyParams({ years: nextValues });
  };

  const handleReset = () => {
    setSearchParams(new URLSearchParams());
    setDrawerOpen(false);
  };

  const results: Anime[] = useMemo(
    () => filterAnime(animeList, { tags, regions, years: years.map(Number), sort }),
    [tags.join(","), regions.join(","), years.join(","), sort]
  );

  const pages = totalPages(results.length, PAGE_SIZE);
  const current = paginate(results, Math.min(page, pages), PAGE_SIZE);
  const activeCount = tags.length + regions.length + years.length;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full border-b border-background-200/80 bg-background-100/60">
          <div className="mx-auto w-full max-w-page px-4 pb-8 pt-8 md:px-6 md:pb-10 md:pt-10">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">动漫分类</span>
            </nav>

            <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              按类型浏览动漫
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-foreground-600">
              支持类型、地区、年份多选筛选，也可以按更新时间、热门度或评分重新排序。所有筛选条件都可以通过链接直接分享。
            </p>
          </div>
        </section>

        <div className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            <aside className="hidden w-full shrink-0 lg:block lg:w-64">
              <div className="sticky top-28 rounded-lg border border-background-200/80 bg-background-50 p-5">
                <div className="mb-4 flex items-center gap-2">
                  <i className="ri-filter-3-line text-base text-primary-600" />
                  <h2 className="font-heading text-sm font-bold text-foreground-950">筛选条件</h2>
                </div>
                <FilterPanel selection={selection} onToggle={handleToggle} onReset={handleReset} />
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              <div className="mb-5 flex flex-col gap-3 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-foreground-600">
                  共 <span className="font-heading font-bold text-foreground-950">{results.length}</span> 部作品
                  {activeCount > 0 && (
                    <span className="ml-1 text-foreground-500">
                      （已应用 {activeCount} 个筛选）
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs font-medium text-foreground-700 lg:hidden"
                  >
                    <i className="ri-filter-3-line" />
                    筛选
                    {activeCount > 0 && (
                      <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-background-50">
                        {activeCount}
                      </span>
                    )}
                  </button>
                  <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                    {sortOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => applyParams({ sort: option.key })}
                        className={[
                          "whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                          sort === option.key
                            ? "bg-background-50 text-primary-600"
                            : "text-foreground-600 hover:text-foreground-900",
                        ].join(" ")}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {current.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
                    {current.map((anime) => (
                      <AnimeCard key={anime.id} anime={anime} />
                    ))}
                  </div>
                  <Pagination
                    page={Math.min(page, pages)}
                    total={pages}
                    onChange={(nextPage) => {
                      applyParams({ page: nextPage });
                      window.scrollTo({ top: 220, behavior: "smooth" });
                    }}
                  />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-20 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                    <i className="ri-emotion-sad-line" />
                  </span>
                  <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                    没有找到符合条件的动漫
                  </h2>
                  <p className="mt-2 max-w-sm text-sm leading-6 text-foreground-600">
                    试着减少筛选条件，或者重置后重新选择类型与年份。
                  </p>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line" />
                    重置筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] flex items-end bg-foreground-950/50 lg:hidden">
          <div className="max-h-[85vh] w-full overflow-y-auto rounded-t-lg bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-lg text-foreground-700"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <FilterPanel selection={selection} onToggle={handleToggle} onReset={handleReset} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50"
            >
              查看 {results.length} 部作品
            </button>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}